import { switchNotationsJson } from "@corca/data";

import { matchVariant } from "./matchVariant";
import { isSignatureMatch, nodeSignatureEntry } from "./signature";

import type { CapturesMap } from "./types";
import type { NodeModel } from "@/models/nodes/types";
import type { SwitchNotationsJsonItem, SwitchNotationVariant } from "@corca/data";

export type MatchedNotation = {
  index: number;
  captures: CapturesMap;
  name: string;
  variants: SwitchNotationVariant[];
};

/**
 * Finds every notation whose one of variants matches `nodes` exactly, returning each with
 * its captures and its *other* variants as conversion targets.
 *
 * A single expression can match multiple notations (e.g. `1/x²` matches the fraction variant
 * of several notations), so we collect them all.
 */
export function getMatchVariants(
  nodes: NodeModel[],
  notations: SwitchNotationsJsonItem[],
): MatchedNotation[] {
  const matches: MatchedNotation[] = [];

  for (const notation of notations) {
    for (let i = 0; i < notation.variants.length; i++) {
      const result = matchVariant(notation.variants[i], nodes);

      if (result) {
        matches.push({
          index: i,
          captures: result.captures,
          name: notation.name,
          variants: notation.variants.filter((_, idx) => idx !== i),
        });
        break;
      }
    }
  }

  return matches;
}

export function filterBySignature(nodes: NodeModel[]): SwitchNotationsJsonItem[] {
  return switchNotationsJson.filter((notation) =>
    notation.variants.some((variant) => isSignatureMatch(variant.signature, nodes)),
  );
}

/**
 * Index from a concrete signature entry (`operator:power`, `operatorSymbol:/`, …) to the
 * notations that contain it. Built once — lets cursor mode look up candidate notations by the
 * operator next to the cursor instead of scanning every notation, so it scales as the library grows.
 */
const notationsByAnchorEntry = buildAnchorIndex();

function buildAnchorIndex(): Map<string, SwitchNotationsJsonItem[]> {
  const index = new Map<string, SwitchNotationsJsonItem[]>();

  for (const notation of switchNotationsJson) {
    const entries = new Set<string>();

    for (const variant of notation.variants) {
      for (const entry of variant.signature) {
        if (entry !== "placeholder") {
          entries.add(entry);
        }
      }
    }

    for (const entry of entries) {
      const list = index.get(entry) ?? [];
      list.push(notation);
      index.set(entry, list);
    }
  }

  return index;
}

/**
 * Notations that use `node` as a concrete anchor — the cursor-mode early bail.
 */
export function notationsForAnchor(node: NodeModel): SwitchNotationsJsonItem[] {
  return notationsByAnchorEntry.get(nodeSignatureEntry(node)) ?? [];
}
